import { FunctionComponent, useCallback, useEffect, useState } from 'react'
import { useNavigate, useLocation } from 'react-router'
import { Button, Alert } from 'react-bootstrap'
import Icon from 'components/ui/icon'

import Page from 'components/ui/page'
import PageHeader from 'components/ui/pageHeader'
import EmptyState from 'components/ui/emptyState'
import * as api from './api.client'
import * as accessRequestApi from '../accessRequest/api.client'
import ConfirmationModal from '../../components/modal/confirmationModal'
import { HospitalDoctor } from './types'
import { AppMessage } from '../../utilities/messages'
import { useHospitalAdmin } from './hospitalAdminContext'
import ManagedHospitalSelect from './components/managedHospitalSelect'
import DoctorsTable from './components/doctorsTable'

const HospitalDoctors: FunctionComponent = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { selectedHospital, requiresHospitalSelection } = useHospitalAdmin()
  const [doctors, setDoctors] = useState<HospitalDoctor[]>([])
  const [doctorsLoaded, setDoctorsLoaded] = useState(false)
  const [showRevokeAccessConfirmation, setShowRevokeAccessConfirmation] =
    useState(false)
  const [appMessage, setAppMessage] = useState<AppMessage>({ show: false })
  const [revokingAccessDoctor, setRevokingAccessDoctor] = useState(false)
  const [revokingAccessDoctorId, setRevokingAccessDoctorId] = useState<number>()
  const [sendingPasswordEmailDoctorId, setSendingPasswordEmailDoctorId] =
    useState<number>()

  const showDoctorAddedMessage = useCallback(() => {
    if (location.state?.newDoctorAdded) {
      setAppMessage({
        show: true,
        success: true,
        message: location.state.userNotified
          ? `Médico adicionado com sucesso. Enviamos um email para o médico (${location.state.email}) solicitando a confirmação do cadastro.`
          : 'Médico adicionado com sucesso. O usuário não foi notificado, mas você poderá fazer isso a qualquer momento.',
      })
    } else if (location.state?.doctorUpdated) {
      setAppMessage({
        show: true,
        success: true,
        message: 'Médico atualizado com sucesso.',
      })
    }
  }, [location.state])

  const getDoctors = useCallback(async () => {
    if (!selectedHospital) {
      setDoctors([])
      return
    }

    try {
      const doctors = await api.getDoctors(selectedHospital.hospitalId)
      setDoctors(doctors)
    } catch (error) {
      setAppMessage({
        show: true,
        message: `Erro ao buscar os médicos: ${(error as any).messages.join(
          ', '
        )}`,
      })
    } finally {
      setDoctorsLoaded(true)
    }
  }, [selectedHospital])

  useEffect(() => {
    showDoctorAddedMessage()
    getDoctors()
  }, [showDoctorAddedMessage, getDoctors])

  const addDoctor = () => navigate('/doctors/new')

  const editDoctor = (doctorId: number) => navigate(`/doctors/${doctorId}`)

  const sendCreatePasswordEmail = async (doctorId: number) => {
    try {
      setSendingPasswordEmailDoctorId(doctorId)
      await api.sendCreatePasswordEmail(doctorId)
      setAppMessage({
        show: true,
        success: true,
        message: 'Email para criação de senha enviado com sucesso.',
      })
    } catch (error) {
      setAppMessage({
        show: true,
        success: false,
        message: 'Erro ao enviar o email para criação de senha.',
      })
    } finally {
      setSendingPasswordEmailDoctorId(undefined)
    }
  }

  const confirmRevokeAccess = (doctorId: number) => {
    setShowRevokeAccessConfirmation(true)
    setRevokingAccessDoctorId(doctorId)
  }

  const revokeDoctorAccess = async () => {
    if (!revokingAccessDoctorId || !selectedHospital) {
      return
    }

    try {
      setRevokingAccessDoctor(true)
      await accessRequestApi.revokeAccessRequestByDoctor(
        selectedHospital.hospitalId,
        revokingAccessDoctorId
      )
      await getDoctors()
    } catch (error) {
      setAppMessage({
        show: true,
        success: false,
        message: 'Erro ao revogar o acesso do médico aos repasses do hospital.',
      })
    } finally {
      setRevokingAccessDoctor(false)
      setShowRevokeAccessConfirmation(false)
      setRevokingAccessDoctorId(undefined)
    }
  }

  const addDoctorButton = (
    <Button variant="primary" onClick={addDoctor} disabled={!selectedHospital}>
      <Icon name="plus" size={16} /> Adicionar médico
    </Button>
  )

  const renderContent = () => {
    if (requiresHospitalSelection) {
      return (
        <EmptyState
          icon={<Icon name="hospital" size={20} />}
          title="Nenhum hospital selecionado"
          description="Selecione o hospital para gerenciar os médicos."
          action={<ManagedHospitalSelect />}
        />
      )
    }

    if (doctorsLoaded && !doctors.length) {
      return (
        <EmptyState
          icon={<Icon name="doctor" size={20} />}
          title="Nenhum médico cadastrado"
          action={addDoctorButton}
        />
      )
    }

    return (
      <DoctorsTable
        doctors={doctors}
        onEdit={editDoctor}
        onSendPassword={sendCreatePasswordEmail}
        onRevokeAccess={confirmRevokeAccess}
        sendingPasswordDoctorId={sendingPasswordEmailDoctorId}
        revokingAccessDoctorId={
          revokingAccessDoctor ? revokingAccessDoctorId : undefined
        }
      />
    )
  }

  return (
    <Page fill>
      <PageHeader
        title="Médicos"
        subtitle="Médicos com acesso aos seus repasses no hospital."
        actions={
          <div className="d-flex flex-wrap gap-2">
            {!requiresHospitalSelection && <ManagedHospitalSelect />}
            {addDoctorButton}
          </div>
        }
      />

      {appMessage.show && (
        <Alert variant={appMessage.success ? 'success' : 'danger'}>
          {appMessage.message}
        </Alert>
      )}

      {showRevokeAccessConfirmation && (
        <ConfirmationModal
          message="Você deseja realmente revogar o acesso do médico aos repasses do hospital?"
          btnMessage="Revogar acesso"
          btnVariant="outline-danger"
          onClose={() => setShowRevokeAccessConfirmation(false)}
          onConfirmation={revokeDoctorAccess}
        />
      )}

      {renderContent()}
    </Page>
  )
}

export default HospitalDoctors
